import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, Users, CreditCard, TrendingDown, CheckCircle2, XCircle, Search } from 'lucide-react';
import { get, post } from '@/shared/api/api_client';
import { Navbar } from '@/widgets/Navbar';
import { Button, Card, Badge, Input } from '@/shared/ui';
import { cn } from '@/shared/utils/cn';
import { formatDate } from '@/shared/utils/format';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface PlatformMetrics {
  totalFamilies: number;
  activeSubscribers: number;
  pendingRequests: number;
  churnRate: number; // percentage
}

interface FamilySizeRequest {
  id: string;
  familyId: string;
  familyName: string;
  currentCount: number;
  requestedCount: number;
  reason: string;
  submittedAt: string;
  status: 'pending' | 'approved' | 'rejected';
}

interface AdminFamily {
  id: string;
  name: string;
  primaryParentEmail: string;
  memberCount: number;
  subscriptionStatus: string;
  createdAt: string;
}

interface MetricsResponse {
  metrics: PlatformMetrics;
}

interface SizeRequestsResponse {
  requests: FamilySizeRequest[];
}

interface FamiliesResponse {
  families: AdminFamily[];
  total: number;
}

// ---------------------------------------------------------------------------
// Metric card
// ---------------------------------------------------------------------------
function MetricCard({
  label,
  value,
  icon,
  suffix,
  colorClass,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  suffix?: string;
  colorClass?: string;
}): React.ReactElement {
  return (
    <Card className="flex flex-col gap-3">
      <div className={cn('flex h-10 w-10 items-center justify-center rounded-xl', colorClass ?? 'bg-primary-50 text-primary-600')}>
        {icon}
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900 tabular-nums">
          {value.toLocaleString()}{suffix}
        </p>
        <p className="text-sm text-slate-500">{label}</p>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Size request item
// ---------------------------------------------------------------------------
function SizeRequestItem({
  request,
  onApprove,
  onReject,
}: {
  request: FamilySizeRequest;
  onApprove: () => void;
  onReject: (note: string) => void;
}): React.ReactElement {
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectNote, setRejectNote] = useState('');

  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors duration-100">
      <td className="px-4 py-3 text-sm font-medium text-slate-900">{request.familyName}</td>
      <td className="px-4 py-3 text-sm text-slate-600 tabular-nums">{request.currentCount}</td>
      <td className="px-4 py-3 text-sm text-slate-600 tabular-nums">{request.requestedCount}</td>
      <td className="px-4 py-3 text-sm text-slate-500 max-w-[200px]">
        <p className="truncate" title={request.reason}>{request.reason}</p>
      </td>
      <td className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap">{formatDate(request.submittedAt)}</td>
      <td className="px-4 py-3">
        {showRejectInput ? (
          <div className="flex flex-col gap-2 min-w-[220px]">
            <input
              type="text"
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              placeholder="Rejection note..."
              className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-danger-400"
              aria-label="Rejection note"
            />
            <div className="flex gap-1">
              <Button
                size="sm"
                variant="danger"
                onClick={() => { onReject(rejectNote); setShowRejectInput(false); }}
              >
                Confirm
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowRejectInput(false)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="primary"
              className="bg-secondary-600 hover:bg-secondary-700 focus-visible:ring-secondary-500"
              onClick={onApprove}
              aria-label={`Approve request for ${request.familyName}`}
            >
              <CheckCircle2 size={13} aria-hidden="true" />
              Approve
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={() => setShowRejectInput(true)}
              aria-label={`Reject request for ${request.familyName}`}
            >
              <XCircle size={13} aria-hidden="true" />
              Reject
            </Button>
          </div>
        )}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// AdminPortalPage
// ---------------------------------------------------------------------------
export default function AdminPortalPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [familySearch, setFamilySearch] = useState('');

  // Queries
  const { data: metricsData, isLoading: metricsLoading } = useQuery<MetricsResponse>({
    queryKey: ['admin', 'metrics'],
    queryFn: () => get<MetricsResponse>('/admin/metrics'),
  });

  const { data: requestsData, isLoading: requestsLoading } = useQuery<SizeRequestsResponse>({
    queryKey: ['admin', 'size-requests'],
    queryFn: () => get<SizeRequestsResponse>('/admin/family-size-requests?status=pending'),
  });

  const { data: familiesData, isLoading: familiesLoading } = useQuery<FamiliesResponse>({
    queryKey: ['admin', 'families', familySearch],
    queryFn: () =>
      get<FamiliesResponse>(`/admin/families${familySearch ? `?search=${encodeURIComponent(familySearch)}` : ''}`),
  });

  // Mutations
  const approveRequestMutation = useMutation({
    mutationFn: (requestId: string) =>
      post<void>(`/admin/family-size-requests/${requestId}/approve`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin'] }),
  });

  const rejectRequestMutation = useMutation({
    mutationFn: ({ requestId, note }: { requestId: string; note: string }) =>
      post<void>(`/admin/family-size-requests/${requestId}/reject`, { note }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin'] }),
  });

  const suspendFamilyMutation = useMutation({
    mutationFn: (familyId: string) => post<void>(`/admin/families/${familyId}/suspend`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'families'] }),
  });

  const reinstateFamilyMutation = useMutation({
    mutationFn: (familyId: string) => post<void>(`/admin/families/${familyId}/reinstate`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'families'] }),
  });

  const metrics = metricsData?.metrics;
  const requests = requestsData?.requests ?? [];
  const families = familiesData?.families ?? [];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 space-y-8">
        {/* Header */}
        <div className="flex items-center gap-2">
          <ShieldCheck size={22} className="text-danger-600" aria-hidden="true" />
          <h1 className="text-xl font-bold text-slate-900">Admin Portal</h1>
        </div>

        {/* Metrics */}
        <section aria-label="Platform metrics">
          <h2 className="mb-3 text-sm font-semibold text-slate-500 uppercase tracking-wide">
            Platform Overview
          </h2>
          {metricsLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[1, 2, 3, 4].map((i) => (
                <Card key={i} className="animate-pulse h-28">{null}</Card>
              ))}
            </div>
          ) : metrics ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <MetricCard
                label="Total Families"
                value={metrics.totalFamilies}
                icon={<Users size={20} aria-hidden="true" />}
                colorClass="bg-primary-50 text-primary-600"
              />
              <MetricCard
                label="Active Subscribers"
                value={metrics.activeSubscribers}
                icon={<CreditCard size={20} aria-hidden="true" />}
                colorClass="bg-secondary-50 text-secondary-600"
              />
              <MetricCard
                label="Pending Requests"
                value={metrics.pendingRequests}
                icon={<CheckCircle2 size={20} aria-hidden="true" />}
                colorClass="bg-warning-50 text-warning-600"
              />
              <MetricCard
                label="Churn Rate"
                value={metrics.churnRate}
                suffix="%"
                icon={<TrendingDown size={20} aria-hidden="true" />}
                colorClass="bg-danger-50 text-danger-600"
              />
            </div>
          ) : (
            <p className="text-sm text-slate-500">Unable to load metrics.</p>
          )}
        </section>

        {/* Family size requests */}
        <section aria-label="Pending family size requests">
          <h2 className="mb-3 text-sm font-semibold text-slate-500 uppercase tracking-wide">
            Pending Family Size Requests
            {requests.length > 0 && (
              <span className="ml-2 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-warning-500 px-1.5 text-[11px] font-bold text-white">
                {requests.length}
              </span>
            )}
          </h2>
          {requestsLoading ? (
            <div className="animate-pulse h-32 rounded-xl bg-white border border-slate-200" />
          ) : requests.length === 0 ? (
            <Card className="flex flex-col items-center gap-2 py-8 text-center">
              <CheckCircle2 size={28} className="text-secondary-400" aria-hidden="true" />
              <p className="text-sm text-slate-500">No pending family size requests.</p>
            </Card>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-card">
              <table className="w-full text-sm" aria-label="Family size requests">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Family</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Current</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Requested</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Reason</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Date</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((request) => (
                    <SizeRequestItem
                      key={request.id}
                      request={request}
                      onApprove={() => approveRequestMutation.mutate(request.id)}
                      onReject={(note) => rejectRequestMutation.mutate({ requestId: request.id, note })}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Family management */}
        <section aria-label="Family management">
          <div className="flex items-center justify-between mb-3 gap-4 flex-wrap">
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
              Family Management
            </h2>
            <div className="w-full max-w-xs">
              <Input
                placeholder="Search families..."
                value={familySearch}
                onChange={(e) => setFamilySearch(e.target.value)}
                leftIcon={<Search size={15} />}
                aria-label="Search families"
              />
            </div>
          </div>

          {familiesLoading ? (
            <div className="animate-pulse h-48 rounded-xl bg-white border border-slate-200" />
          ) : families.length === 0 ? (
            <Card className="flex flex-col items-center gap-2 py-8 text-center">
              <Users size={28} className="text-slate-300" aria-hidden="true" />
              <p className="text-sm text-slate-500">No families found.</p>
            </Card>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-card">
              <table className="w-full text-sm" aria-label="Families">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Family</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Primary Parent</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Members</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Created</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {families.map((family) => {
                    const isSuspended = family.subscriptionStatus === 'suspended';
                    return (
                      <tr key={family.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors duration-100">
                        <td className="px-4 py-3 font-medium text-slate-900">{family.name}</td>
                        <td className="px-4 py-3 text-slate-600 truncate max-w-[160px]">{family.primaryParentEmail}</td>
                        <td className="px-4 py-3 text-slate-600 tabular-nums">{family.memberCount}</td>
                        <td className="px-4 py-3">
                          <Badge
                            variant={
                              family.subscriptionStatus === 'active'
                                ? 'success'
                                : family.subscriptionStatus === 'trialing'
                                ? 'info'
                                : family.subscriptionStatus === 'suspended'
                                ? 'danger'
                                : 'neutral'
                            }
                          >
                            {family.subscriptionStatus.charAt(0).toUpperCase() + family.subscriptionStatus.slice(1)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap">
                          {formatDate(family.createdAt)}
                        </td>
                        <td className="px-4 py-3">
                          {isSuspended ? (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => reinstateFamilyMutation.mutate(family.id)}
                              aria-label={`Reinstate ${family.name}`}
                            >
                              Reinstate
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-danger-600 hover:bg-danger-50"
                              onClick={() => suspendFamilyMutation.mutate(family.id)}
                              aria-label={`Suspend ${family.name}`}
                            >
                              Suspend
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
